import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Package, ShoppingCart, CreditCard, Truck, Filter, MessageCircle, Smartphone, Sparkles, DollarSign, Palette } from 'lucide-react';
import Footer from '../components/Footer';

const LandingPage = () => {

    // El CTA flotante aparece recien cuando el CTA del hero quedo atras,
    // para no duplicar el boton en la primera pantalla.
    const [showFloatingCta, setShowFloatingCta] = useState(false);

    useEffect(() => {
        const onScroll = () => setShowFloatingCta(window.scrollY > 600);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const features = [
        {
            icon: <Package className="w-8 h-8" />,
            title: "Catálogo Completo",
            description: "Todos los productos Essen con imágenes, descripciones y especificaciones técnicas detalladas"
        },
        {
            icon: <Filter className="w-8 h-8" />,
            title: "Búsqueda Inteligente",
            description: "Filtra productos por línea, color y busca por nombre para encontrar exactamente lo que necesitás"
        },
        {
            icon: <ShoppingCart className="w-8 h-8" />,
            title: "Sistema de Combos",
            description: "Armá tu combo ideal agregando múltiples productos y consultá todo junto por WhatsApp"
        },
        {
            icon: <MessageCircle className="w-8 h-8" />,
            title: "Contacto Directo",
            description: "Consultá productos directamente por WhatsApp con toda la información del precio y forma de pago"
        },
        {
            icon: <CreditCard className="w-8 h-8" />,
            title: "Cuotas sin Interés",
            description: "Calculadora integrada que muestra todas las opciones de cuotas disponibles según tu tarjeta"
        },
        {
            icon: <DollarSign className="w-8 h-8" />,
            title: "Reintegros",
            description: "Visualizá el reintegro que obtenés con tarjetas seleccionadas antes de consultar"
        },
        {
            icon: <Truck className="w-8 h-8" />,
            title: "Envío Calculado",
            description: "El costo de envío se suma automáticamente al total y se bonifica al superar los puntos mínimos"
        },
        {
            icon: <Palette className="w-8 h-8" />,
            title: "Totalmente Personalizable",
            description: "Adaptá el catálogo a tu marca personal agregando tu propio logo y eligiendo tus colores favoritos"
        },
        {
            icon: <Smartphone className="w-8 h-8" />,
            title: "En cualquier dispositivo",
            description: "Funciona perfectamente en celulares, tablets y computadoras con diseño optimizado"
        }
    ];

    const benefits = [
        "URL personalizada con tu número Essen",
        "Actualización de productos en tiempo real",
        "Filtros por línea, color y búsqueda por nombre",
        "Calculadora de cuotas integrada",
        "Sistema de combos para consultas múltiples",
        "Visualización de reintegros por tarjeta",
        "Cálculo automático de envío con bonificación por puntos",
        "Contacto directo por WhatsApp",
        "Personalización de logo y colores"
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
            <Helmet>
                <title>Catálogo Essen Digital | Productos, Precios y Cuotas Sin Interés</title>
                <meta name="description" content="Catálogo digital profesional de productos Essen. Explorá todos los productos, compará precios en efectivo y tarjeta, calculá cuotas y contactá por WhatsApp." />
                <link rel="canonical" href="https://miessen.shop/" />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://miessen.shop/" />
                <meta property="og:title" content="Catálogo Essen Digital" />
                <meta property="og:description" content="Catálogo profesional de productos Essen con precios y cuotas" />
                <meta property="og:image" content="https://miessen.shop/og-image.jpg" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:site_name" content="Mi Essen" />
                <meta property="og:locale" content="es_AR" />
                <meta name="twitter:card" content="summary_large_image" />
                <link rel="alternate" hreflang="es-AR" href="https://miessen.shop/" />
                <script type="application/ld+json">
                    {`
                        {
                            "@context": "https://schema.org",
                            "@type": "WebSite",
                            "name": "Catálogo Essen Digital",
                            "url": "https://miessen.shop/",
                            "description": "Catálogo digital profesional de productos Essen. Explorá todos los productos, compará precios en efectivo y tarjeta, calculá cuotas y contactá por WhatsApp."
                        }
                    `}
                </script>
            </Helmet>
            {/* Hero Section */}
            <header className="bg-white/80 backdrop-blur-lg shadow-sm border-b border-purple-100">
                <div className="max-w-7xl mx-auto px-4 py-6">
                    <div className="flex items-center justify-center gap-3">
                        <Package className="w-10 h-10 text-purple-600" aria-hidden="true" />
                        <span className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent py-1">
                            Catálogo Essen Digital
                        </span>
                    </div>
                </div>
            </header>

            <main>
                {/* Hero Content */}
                <section className="max-w-7xl mx-auto px-4 pt-16 pb-20 text-center">
                    <div className="flex items-center justify-center gap-2 mb-4">
                        <Sparkles className="w-8 h-8 text-purple-600" aria-hidden="true" />
                        <span className="text-purple-600 font-semibold text-lg">La mejor experiencia de compra</span>
                    </div>
                    <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                        Tu Catálogo Essen
                        <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 py-5">
                            Siempre Disponible
                        </span>
                    </h1>
                    <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
                        Potenciá tus ventas con un catálogo digital profesional.
                        <strong className="text-gray-800"> Tus clientes pueden ver productos, comparar precios en efectivo y tarjeta,
                            calcular cuotas, armar combos y contactarte al instante.</strong>
                    </p>
                    <a
                        href="https://wa.me/5492616815848?text=Hola!%20Me%20interesa%20contratar%20el%20servicio%20de%20catálogo%20digital"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-3 bg-green-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-green-700 transition-all shadow-lg hover:shadow-xl hover:scale-105"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden="true">
                            <path d="M20.52 3.48A11.87 11.87 0 0012 .5C6.21.5 1.5 5.21 1.5 11c0 1.96.51 3.86 1.47 5.55L.5 23.5l6.98-1.82A11.87 11.87 0 0012 22.5c5.79 0 10.5-4.71 10.5-10.5 0-3.02-1.18-5.82-3.98-7.52zM12 20.5c-1.07 0-2.12-.24-3.08-.7l-.22-.11-4.15 1.09 1.12-3.98-.14-.25A8.37 8.37 0 013.5 11c0-4.7 3.8-8.5 8.5-8.5 4.7 0 8.5 3.8 8.5 8.5S16.7 20.5 12 20.5z" />
                            <path d="M17.44 14.03c-.29-.14-1.71-.84-1.98-.94-.27-.1-.47-.14-.67.14-.19.29-.73.94-.9 1.13-.16.19-.33.21-.62.07-.29-.14-1.22-.45-2.32-1.43-.86-.77-1.44-1.72-1.61-2.01-.17-.29-.02-.45.13-.6.13-.13.29-.33.43-.5.14-.17.19-.28.29-.47.1-.19.05-.35-.02-.49-.07-.14-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.19 0-.5.07-.76.34-.26.26-1 1-1 2.44 0 1.44 1.03 2.83 1.17 3.03.14.19 2.02 3.09 4.9 4.33 2.67 1.13 2.67.75 3.15.7.48-.05 1.55-.63 1.77-1.24.22-.61.22-1.12.15-1.24-.07-.12-.27-.19-.56-.33z" />
                        </svg>
                        Contratar Servicio
                    </a>
                </section>


                {/* Features Section */}
                <section className="max-w-7xl mx-auto px-4 pb-20">
                    <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl p-8 md:p-12 text-white shadow-2xl">
                        <h3 className="text-3xl md:text-4xl font-bold text-center mb-4">
                            Todo lo que tus clientes necesitan
                        </h3>
                        <p className="text-center text-purple-100 mb-12 text-lg max-w-2xl mx-auto">
                            Una experiencia de compra completa y profesional
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {features.map((feature, index) => (
                                <div
                                    key={index}
                                    className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all hover:-translate-y-1"
                                >
                                    <div className="bg-white/20 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                                        {feature.icon}
                                    </div>
                                    <h4 className="text-lg font-bold mb-2">
                                        {feature.title}
                                    </h4>
                                    <p className="text-purple-100 text-sm leading-relaxed">
                                        {feature.description}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Benefits Section */}
                <section className="max-w-7xl mx-auto px-4 pb-20">
                    <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-gray-100">
                        <h3 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
                            Todo incluido en tu catálogo
                        </h3>
                        <p className="text-center text-gray-600 mb-12 text-lg">
                            Sin costos ocultos, todas las funcionalidades disponibles
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
                            {benefits.map((benefit, index) => (
                                <div key={index} className="flex items-start gap-3 bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-100">
                                    <div className="bg-green-500 rounded-full p-1 flex-shrink-0 mt-0.5">
                                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <span className="text-gray-800 font-medium">{benefit}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="max-w-7xl mx-auto px-4 pb-20">
                    <div className="bg-gradient-to-br from-gray-900 via-purple-950 to-gray-900 rounded-3xl p-8 md:p-12 text-center text-white shadow-2xl">
                        <h3 className="text-3xl md:text-4xl font-bold mb-4">
                            ¿Lista para potenciar tus ventas?
                        </h3>
                        <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                            Empezá hoy mismo con tu catálogo digital personalizado
                        </p>
                        <a
                            href="https://wa.me/5492616815848?text=Hola!%20Me%20interesa%20contratar%20el%20servicio%20de%20catálogo%20digital"
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-3 bg-green-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-green-700 transition-all shadow-lg hover:shadow-xl hover:scale-105"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden="true">
                                <path d="M20.52 3.48A11.87 11.87 0 0012 .5C6.21.5 1.5 5.21 1.5 11c0 1.96.51 3.86 1.47 5.55L.5 23.5l6.98-1.82A11.87 11.87 0 0012 22.5c5.79 0 10.5-4.71 10.5-10.5 0-3.02-1.18-5.82-3.98-7.52zM12 20.5c-1.07 0-2.12-.24-3.08-.7l-.22-.11-4.15 1.09 1.12-3.98-.14-.25A8.37 8.37 0 013.5 11c0-4.7 3.8-8.5 8.5-8.5 4.7 0 8.5 3.8 8.5 8.5S16.7 20.5 12 20.5z" />
                                <path d="M17.44 14.03c-.29-.14-1.71-.84-1.98-.94-.27-.1-.47-.14-.67.14-.19.29-.73.94-.9 1.13-.16.19-.33.21-.62.07-.29-.14-1.22-.45-2.32-1.43-.86-.77-1.44-1.72-1.61-2.01-.17-.29-.02-.45.13-.6.13-.13.29-.33.43-.5.14-.17.19-.28.29-.47.1-.19.05-.35-.02-.49-.07-.14-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.19 0-.5.07-.76.34-.26.26-1 1-1 2.44 0 1.44 1.03 2.83 1.17 3.03.14.19 2.02 3.09 4.9 4.33 2.67 1.13 2.67.75 3.15.7.48-.05 1.55-.63 1.77-1.24.22-.61.22-1.12.15-1.24-.07-.12-.27-.19-.56-.33z" />
                            </svg>
                            Contactar por WhatsApp
                        </a>
                    </div>
                </section>
            </main>

            {/* CTA flotante solo en mobile */}
            <a
                href="https://wa.me/5492616815848?text=Hola!%20Me%20interesa%20contratar%20el%20servicio%20de%20catálogo%20digital"
                target="_blank"
                rel="noreferrer"
                className={`md:hidden fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 bg-green-600 text-white pl-4 pr-5 py-3 rounded-full font-bold shadow-xl hover:bg-green-700 active:scale-95 transition-all ${showFloatingCta ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden="true">
                    <path d="M20.52 3.48A11.87 11.87 0 0012 .5C6.21.5 1.5 5.21 1.5 11c0 1.96.51 3.86 1.47 5.55L.5 23.5l6.98-1.82A11.87 11.87 0 0012 22.5c5.79 0 10.5-4.71 10.5-10.5 0-3.02-1.18-5.82-3.98-7.52zM12 20.5c-1.07 0-2.12-.24-3.08-.7l-.22-.11-4.15 1.09 1.12-3.98-.14-.25A8.37 8.37 0 013.5 11c0-4.7 3.8-8.5 8.5-8.5 4.7 0 8.5 3.8 8.5 8.5S16.7 20.5 12 20.5z" />
                    <path d="M17.44 14.03c-.29-.14-1.71-.84-1.98-.94-.27-.1-.47-.14-.67.14-.19.29-.73.94-.9 1.13-.16.19-.33.21-.62.07-.29-.14-1.22-.45-2.32-1.43-.86-.77-1.44-1.72-1.61-2.01-.17-.29-.02-.45.13-.6.13-.13.29-.33.43-.5.14-.17.19-.28.29-.47.1-.19.05-.35-.02-.49-.07-.14-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.19 0-.5.07-.76.34-.26.26-1 1-1 2.44 0 1.44 1.03 2.83 1.17 3.03.14.19 2.02 3.09 4.9 4.33 2.67 1.13 2.67.75 3.15.7.48-.05 1.55-.63 1.77-1.24.22-.61.22-1.12.15-1.24-.07-.12-.27-.19-.56-.33z" />
                </svg>
                Contratar
            </a>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default LandingPage;
